import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import integrationsService from '../../services/integrationsService';
import hotelService from '../../services/hotelService';
import SearchSelect from '../shared/SearchSelect';
import { apiErrorMessage } from '../../utils/apiError';
import '../IntegrationForm.css';                  // .ifm-impl-row
import '../shared/ImplementationWizard.css';      // .iw-empty — an implementation's unfilled count

// PIPELINES › HOTELS — the hotels that run pipelines, one row per implementation (a hotel + a pipeline template).
//
// ADDING ONE IS PICKING THE TWO: a hotel of the chain that runs no pipeline yet (a hotel runs ONE — the server refuses
// a second) and a template. The implementation is created
// at once as a draft (the server issues its Universal API credential) and opened — its board shows every credential it
// still needs in red. Nothing routes until it is switched to active there.
//
// Scoped by the navbar: its hotel when one is chosen, otherwise its chain. A template belongs to the chain (V524), so the
// templates offered are the chain's.
export const implementationPath = (im) => `/pipelines/${im.template_id}/implementations/${im.id}`;

export default function PipelineHotelsTab({ partnerId, accountId, hotelId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);        // null = not loaded
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [hotels, setHotels] = useState(null);
  const [addHotelId, setAddHotelId] = useState(null);
  const [addTemplateId, setAddTemplateId] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await integrationsService.listHotelImplementations(
        { partner_id: partnerId, account_id: accountId, hotel_id: hotelId || undefined });
      setRows(res.implementations || []);
    } catch (e) {
      setError(apiErrorMessage(e, 'The hotels running pipelines could not be loaded.'));
      setRows([]);
    }
  }, [partnerId, accountId, hotelId]);
  useEffect(() => { load(); }, [load]);

  // What can be added: the chain's hotels, and the chain's templates.
  useEffect(() => {
    let cancelled = false;
    setTemplates(null); setHotels(null);
    integrationsService.getAllIntegrations({ partner_id: partnerId, account_id: accountId })
      // A disabled template (V693) takes no new hotel — the server refuses it too.
      .then(res => { if (!cancelled) setTemplates((res.integrations || []).filter(t => !t.mcp_source_system_id && Number(t.enabled ?? 1))); })
      .catch(() => { if (!cancelled) setTemplates([]); });
    hotelService.getAllHotels({ accountid: accountId })
      .then(data => {
        if (cancelled) return;
        setHotels((data.hotels || data || []).map(h => ({ id: h.hotelid, label: h.hotelname, code: h.hotelcode || undefined })));
      })
      .catch(() => { if (!cancelled) setHotels([]); });
    return () => { cancelled = true; };
  }, [partnerId, accountId]);
  // ONE PIPELINE PER HOTEL: only hotels not in the list are offered (and only the navbar's, when one is chosen). A row on
  // a DISABLED template does not count (V693) — that hotel is free to join another.
  const hotelItems = useMemo(() => {
    const taken = new Set((rows || []).filter(r => Number(r.template_enabled ?? 1)).map(r => String(r.hotel_id)));
    return (hotels || []).filter(h => !taken.has(String(h.id)) && (!hotelId || String(h.id) === String(hotelId)));
  }, [hotels, rows, hotelId]);
  useEffect(() => {
    if (addHotelId && !hotelItems.some(h => String(h.id) === String(addHotelId))) setAddHotelId(null);
  }, [hotelItems, addHotelId]);
  const templateItems = useMemo(() => (templates || []).map(t => ({ id: t.id, label: t.name })), [templates]);

  const add = async () => {
    const hotel = (hotels || []).find(h => String(h.id) === String(addHotelId));
    if (!hotel || !addTemplateId) return;
    setAdding(true);
    setError(null);
    try {
      const res = await integrationsService.saveImplementation(addTemplateId, {
        hotelId: hotel.id, name: hotel.label,
        // NOT ACTIVE ON CREATION: it routes nothing until its credentials are set and somebody switches it on.
        status: 'draft', bindings: [],
      });
      const created = res && res.implementation;
      if (created && created.id) navigate(implementationPath({ template_id: addTemplateId, id: created.id }));
      else { setAddTemplateId(null); load(); }
    } catch (e) {
      setError(apiErrorMessage(e, 'That hotel could not be added to the pipeline.'));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        <div style={{ flex: '1 1 240px' }}><SearchSelect
          items={hotelItems}
          value={addHotelId}
          onChange={setAddHotelId}
          disabled={adding || hotels === null}
          placeholder={hotels === null ? 'Loading hotels…' : 'Choose a hotel…'}
          searchPlaceholder="Search hotels…"
          emptyLabel="Every hotel here already runs a pipeline — a hotel runs one."
        /></div>
        <div style={{ flex: '1 1 240px' }}><SearchSelect
          items={templateItems}
          value={addTemplateId}
          onChange={setAddTemplateId}
          disabled={adding || templates === null || !addHotelId}
          placeholder={templates === null ? 'Loading templates…' : 'Choose a pipeline template…'}
          searchPlaceholder="Search templates…"
          emptyLabel="The chain has no pipeline templates yet — create one on the Pipeline Templates tab."
        /></div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={add}
          disabled={!addHotelId || !addTemplateId || adding}
          title="Run this template at this hotel — then set each red credential on its board"
          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          <Plus size={16} /> {adding ? 'Adding…' : 'Add'}
        </button>
      </div>

      {error && <div className="os-msg os-msg-error">{error}</div>}

      {rows === null ? (
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading hotels…</p>
      ) : rows.length === 0 ? (
        <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.5rem', color: '#6b7280', fontSize: '0.925rem' }}>
          {hotelId ? 'This hotel runs no pipelines yet.' : 'No hotel in this chain runs a pipeline yet.'} Choose a hotel and a
          pipeline template and <strong>Add</strong> it.
        </div>
      ) : (
        <table className="acp-table">
          <thead>
            <tr><th>Hotel</th><th>Pipeline template</th><th>Version</th><th>Status</th><th>Filled</th></tr>
          </thead>
          <tbody>
            {rows.map(im => (
              <tr
                key={im.id}
                className="ifm-impl-row"
                onClick={() => navigate(implementationPath(im))}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(implementationPath(im)); } }}
                tabIndex={0}
                role="button"
                title={`Open ${im.hotelname || im.name} — ${im.template_name}`}
              >
                <td>
                  <div style={{ fontWeight: 600 }}>{im.hotelname || <em>no property</em>}</div>
                  {im.chain_name && <div className="form-help" style={{ margin: 0 }}>{im.chain_name}</div>}
                </td>
                <td>
                  {im.template_name}
                  {!Number(im.template_enabled ?? 1) && <div className="form-help" style={{ margin: 0 }}>template disabled</div>}
                </td>
                <td>
                  {im.version ? `v${im.version}` : <span className="form-help" style={{ margin: 0 }}>draft</span>}
                  {im.version && im.live_version && im.version !== im.live_version && (
                    <div className="form-help" style={{ margin: 0 }}>v{im.live_version} is live</div>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${im.status === 'active' ? 'status-active' : 'status-pending'}`}>{im.status}</span>
                </td>
                <td className={im.filled < im.slots ? 'iw-empty' : ''}>{im.filled} of {im.slots}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
